import { Button } from '../primitives/button.js'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Prev as ArrowLeft, ArrowRight } from '@tunasong/icons'
import type { FilterFunc, NavigationEntity } from '@tunasong/models'
import { isPersistedEntity, matchAny } from '@tunasong/models'
import type { Entity, Persisted } from '@tunasong/schemas'
import { isAudio, isFolderContainer, isMedia, isSong } from '@tunasong/schemas'
import cn from 'classnames'
import type { HTMLAttributes } from 'react'
import { useCallback, useState } from 'react'
import invariant from 'tiny-invariant'
import { DndList } from '../list/dnd-list.js'
import type { NavFn } from '../navigation/navigate.js'
import { EntityTree } from './entity-tree.js'
import styles from './transfer-list.module.css'

export interface TransferListProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  parent?: Persisted<Entity>
  list: Persisted<Entity>[]
  filter?: FilterFunc
  onChange(list: Persisted<Entity>[]): void
}

const defaultFilter = matchAny(isSong, isAudio, isFolderContainer)

export function TransferList(props: TransferListProps) {
  const { list = [], filter: filterFunc = defaultFilter, onChange, ...restProps } = props
  const { entities: rootEntities } = graphHooks.useEntities({ root: true, filter: filterFunc })
  const [selectedList, setCheckedList] = useState<Persisted<Entity>[]>([])
  const [selected, setSelected] = useState<Persisted<Entity>>()

  const handleAdd = useCallback(() => {
    if (!(selected && list)) {
      return
    }
    onChange([...list, selected])
    setSelected(undefined)
  }, [list, onChange, selected])

  const handleRemove = useCallback(() => {
    const c = list.filter(s => !selectedList.includes(s))
    setCheckedList([])
    onChange(c)
  }, [list, onChange, selectedList])

  const handleSelectTree = useCallback((value: Persisted<Entity>) => {
    const val = isMedia(value) ? value : undefined
    setSelected(val)
  }, [])

  const handleSelectList: NavFn = useCallback((value?: NavigationEntity) => {
    invariant(isPersistedEntity(value))
    setCheckedList([value])
  }, [])

  const notDropTarget = useCallback(() => false, [])

  return (
    <div {...restProps} className={cn(styles.root, restProps.className)} style={restProps.style}>
      <div className={styles.panel}>
        <EntityTree rootEntities={rootEntities} selected={selected} filter={filterFunc} onSelect={handleSelectTree} />
      </div>
      <div className={styles.buttonColumn}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleAdd}
          disabled={!selected}
          aria-label="move selected right"
          className={styles.transferButton}
        >
          {/* Greater than sign unicode */}
          <ArrowRight />
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={handleRemove}
          disabled={selectedList.length === 0}
          aria-label="move selected left"
          className={styles.transferButton}
        >
          <ArrowLeft />
        </Button>
      </div>

      {/* Use DnDList for DnD sorting */}
      <DndList
        component="div"
        className={cn(styles.panel, styles.dndList)}
        entities={list}
        isDropTarget={notDropTarget}
        sort={false}
        actions={false}
        onChange={onChange}
        selected={selectedList}
        onOpen={handleSelectList}
        navigate={false}
      />
    </div>
  )
}
